/**
 * Deactivate Center Dialog
 * Handles both blockable and confirmable deactivation flows
 */

import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import type { Location, DeactivatePreCheck } from '../../services/api/locationService';

interface DeactivateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  location: Location | null;
  preCheck: DeactivatePreCheck | null;
  isLoading: boolean;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const AlertBox = styled.div<{ $variant: 'warning' | 'error' }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme, $variant }) =>
    $variant === 'error' ? theme.colors.status.error.bg : theme.colors.status.warning.bg};
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === 'error' ? theme.colors.status.error.border : theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const AlertIcon = styled.div<{ $variant: 'warning' | 'error' }>`
  flex-shrink: 0;
  color: ${({ theme, $variant }) =>
    $variant === 'error' ? theme.colors.status.error.text : theme.colors.status.warning.text};

  svg {
    width: 22px;
    height: 22px;
  }
`;

const AlertBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const AlertTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AlertText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
`;

const BlockerList = styled.ul`
  margin: ${({ theme }) => theme.spacing[2]} 0 0;
  padding-left: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

export const DeactivateDialog = ({
  isOpen,
  onClose,
  onConfirm,
  location,
  preCheck,
  isLoading,
}: DeactivateDialogProps) => {
  if (!location) return null;

  const canDeactivate = preCheck?.canDeactivate ?? false;
  const blockers = preCheck?.blockers;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={canDeactivate ? 'Deactivate Center' : 'Cannot Deactivate Center'}
      size="sm"
      footer={
        canDeactivate ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="danger" onClick={onConfirm} isLoading={isLoading}>
              Deactivate
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Understood
          </Button>
        )
      }
    >
      <Content>
        {canDeactivate ? (
          <AlertBox $variant="warning">
            <AlertIcon $variant="warning">
              <AlertTriangle />
            </AlertIcon>
            <AlertBody>
              <AlertTitle>Are you sure?</AlertTitle>
              <AlertText>
                Deactivating <strong>{location.name}</strong> will prevent new sessions from being
                created at this center. Existing data will be preserved.
              </AlertText>
            </AlertBody>
          </AlertBox>
        ) : (
          <AlertBox $variant="error">
            <AlertIcon $variant="error">
              <AlertCircle />
            </AlertIcon>
            <AlertBody>
              <AlertTitle>Transfer required before deactivation</AlertTitle>
              <AlertText>
                <strong>{location.name}</strong> still has active assignments that must be
                transferred first:
              </AlertText>
              {blockers && (
                <BlockerList>
                  {blockers.activeClinicians > 0 && (
                    <li>
                      {blockers.activeClinicians} clinician
                      {blockers.activeClinicians !== 1 ? 's' : ''} must be transferred
                    </li>
                  )}
                  {blockers.activeClients > 0 && (
                    <li>
                      {blockers.activeClients} client
                      {blockers.activeClients !== 1 ? 's' : ''} must be transferred
                    </li>
                  )}
                </BlockerList>
              )}
            </AlertBody>
          </AlertBox>
        )}
      </Content>
    </Modal>
  );
};
